import {
  AutomationExitRuleEventType as Event,
  AutomationExitRuleKindType as Kind,
  AutomationExitRuleScopeType as Scope,
  type AudienceFilterInputType,
  type AutomationExitRuleInputType,
} from "@/types/graphql/partner.generated";
import {
  emptyAudience,
  type AutomationExitRuleItem,
} from "@/partner/entities/marketing";
import { toFilterInput } from "./filterInput";

/** Правило выхода в форме редактора цепочки. */
export interface ExitRuleForm {
  id: string;
  name: string;
  kind: Kind;
  event: Event | null;
  filter: AudienceFilterInputType | null;
  scope: Scope;
}

/**
 * Новое правило: цель по первому событию из правил сервера (exitRuleEvents)
 * по любому продукту; без событий правило держится на пустом условии. id
 * безопасный для правил сервера (буквы, цифры, дефис) и случайный: id
 * удалённого правила, в том числе удалённого в прошлых сохранениях, не
 * достаётся новому, иначе итог прошлых участников (exitRuleId) подписался
 * бы именем нового правила. Предел числа правил (limits.exitRules)
 * проверяет вызывающий код по правилам сервера, своих пределов модель не
 * держит.
 */
export function newExitRule(
  rules: ExitRuleForm[],
  name: string,
  event: Event | null
): ExitRuleForm {
  const taken = new Set(rules.map((r) => r.id));
  let id: string;
  do {
    id = `rule-${crypto.randomUUID().slice(0, 8)}`;
  } while (taken.has(id));
  return {
    id,
    name,
    kind: Kind.GoalType,
    event,
    filter: event ? null : emptyAudience(),
    scope: Scope.AnyType,
  };
}

/**
 * Правило на шаг выше (-1) или ниже (+1): порядок правил это порядок
 * проверки. За краем списка или с неизвестным id тот же массив.
 */
export function moveExitRule(
  rules: ExitRuleForm[],
  id: string,
  delta: -1 | 1
): ExitRuleForm[] {
  const from = rules.findIndex((r) => r.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= rules.length) return rules;
  const next = [...rules];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/**
 * Смена события правила. Охват CONTEXT остаётся только у события из
 * contextScopeEvents. Условие не теряется и не прячется: сервер проверяет
 * событие И условие, поэтому панель показывает условие и под событием;
 * правило без события держится на условии (пустое для начала).
 */
export function withExitEvent(
  rule: ExitRuleForm,
  event: Event | null,
  contextScopeEvents: readonly Event[]
): ExitRuleForm {
  return {
    ...rule,
    event,
    scope:
      event && contextScopeEvents.includes(event) ? rule.scope : Scope.AnyType,
    filter: rule.filter ?? (event ? null : emptyAudience()),
  };
}

/**
 * Подсказка до ответа сервера (смысл правил проверяет сервер,
 * automationGraphIssues): название, событие или условие, CONTEXT только у
 * события из contextScopeEvents. Предел названия (limits.exitRuleNameLength)
 * и список событий с охватом CONTEXT приходят из правил сервера: своих
 * пределов и списков модель не держит.
 */
export function exitRuleHint(
  rule: ExitRuleForm,
  nameMax: number,
  contextScopeEvents: readonly Event[]
): string | null {
  const name = rule.name.trim();
  // Текст подсказки ждёт {{max}}: вызывающий код передаёт тот же nameMax.
  if (!name || name.length > nameMax) return "automations.exitRules.nameHint";
  const hasFilter = Boolean(rule.filter && rule.filter.conditions.length > 0);
  if (!rule.event && !hasFilter) return "automations.issues.EXIT_RULE_INVALID";
  if (
    rule.scope === Scope.ContextType &&
    (!rule.event || !contextScopeEvents.includes(rule.event))
  ) {
    return "automations.issues.EXIT_RULE_INVALID";
  }
  return null;
}

/** Правила из ответа сервера в форму: фильтр без __typename. */
export const fromExitRules = (
  items: AutomationExitRuleItem[]
): ExitRuleForm[] =>
  items.map((item) => ({
    id: item.id,
    name: item.name,
    kind: item.kind,
    event: item.event ?? null,
    filter: item.filter ? toFilterInput(item.filter) : null,
    scope: item.scope,
  }));

/** Правило во вход мутации: название без пробелов по краям. */
export const toExitRuleInput = (
  rule: ExitRuleForm
): AutomationExitRuleInputType => ({
  id: rule.id,
  name: rule.name.trim(),
  kind: rule.kind,
  event: rule.event,
  // Канонический вид (как у условия шага и аудитории): форма объекта из UI
  // не делает форму грязной и лишние поля не уходят в мутацию. Условие без
  // строк это «без условия»: у события оно ничего не сужает.
  filter:
    rule.filter && rule.filter.conditions.length > 0
      ? toFilterInput(rule.filter)
      : null,
  scope: rule.scope,
});
